import requestV2 from '@/admin/api/requestV2';
import type { Pagination } from '@/admin/api/courts';

export type MatchStatus = 'NOT_STARTED' | 'STARTED' | 'CANCELED' | 'ARCHIVED';
export type SkillLevel = 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED';
export type TimeRange = 'MORNING' | 'MIDDAY' | 'EVENING' | string;

export interface OpenMatchModel {
  id: number;
  matchId?: number; // list response may use matchId instead of id
  ownerId?: number;
  participantIds?: number[];
  countPlayers?: number;
  courtId?: number;
  courtName?: string;
  status: MatchStatus;
  eventDate?: string; // ISO string
  timeRanges?: TimeRange | TimeRange[];
  reviewIds?: number[];
  skillLevel?: SkillLevel;
  commentMessage?: string;
  chatId?: number;
}

export interface MatchesFilters {
  page?: number; // 1-based in UI
  pageSize?: number;
  status?: MatchStatus;
  skillLevel?: SkillLevel;
  dateFrom?: string; // ISO
  dateTo?: string;   // ISO
  courtId?: number;
  courtName?: string;
  participantName?: string;
  city?: string;
}

function toOpenMatch(item: any): OpenMatchModel {
  const id = typeof item.id === 'number' ? item.id : (item.matchId ?? item.id);
  return {
    id,
    matchId: item.matchId,
    ownerId: item.ownerId ?? item?.owner?.id,
    participantIds: item.participantIds ?? (Array.isArray(item?.users) ? item.users.map((u: any) => u?.id).filter((n: any) => typeof n === 'number') : undefined),
    countPlayers: item.countPlayers,
    courtId: item.courtId ?? item?.court?.id,
    courtName: item.courtName ?? item?.court?.name,
    status: item.status as MatchStatus,
    eventDate: item.eventDate,
    timeRanges: item.timeRanges,
    reviewIds: item.reviewIds,
    skillLevel: item.skillLevel as SkillLevel,
    commentMessage: item.commentMessage,
    chatId: item.chatId,
  };
}

export async function listOpenMatches(filters: MatchesFilters): Promise<Pagination<OpenMatchModel>> {
  // Backend uses 0-based paging; convert from 1-based UI if provided
  const zeroBasedPage = Math.max(0, (filters.page ?? 0) - 1);
  const size = filters.pageSize ?? 10;
  const query: Record<string, any> = {
    page: zeroBasedPage,
    size,
  };
  if (filters.status) query.status = filters.status;
  if (filters.skillLevel) query.skillLevel = filters.skillLevel;
  if (filters.dateFrom) query.dateFrom = filters.dateFrom;
  if (filters.dateTo) query.dateTo = filters.dateTo;
  if (filters.courtId != null) query.courtId = filters.courtId;
  if (filters.courtName) query.courtName = filters.courtName;
  if (filters.participantName) query.participantName = filters.participantName;
  if (filters.city) query.city = filters.city;

  const resp = await requestV2.get<any>('/open-matches', { query });

  const rawItems: any[] = Array.isArray(resp)
    ? resp
    : Array.isArray(resp?.items)
      ? resp.items
      : Array.isArray(resp?.content)
        ? resp.content
        : [];

  const items = rawItems.map(toOpenMatch);
  const total = (resp && (resp.total ?? resp.count ?? resp.totalElements)) as number | undefined;
  const backendPage = (resp && (resp.page ?? resp.pageNumber)) as number | undefined;
  const outPage = ((backendPage ?? zeroBasedPage) + 1); // back to 1-based
  const outPageSize = (resp && (resp.pageSize ?? resp.size)) || size;
  const pageCount = (resp && (resp.pageCount ?? resp.countPage ?? resp.totalPages)) as number | undefined;

  return { items, total, page: outPage, pageSize: outPageSize, pageCount };
}

export async function getOpenMatch(id: number): Promise<OpenMatchModel> {
  const data = await requestV2.get<any>(`/open-matches/${id}`);
  return toOpenMatch(data);
}

export async function completeOpenMatch(id: number): Promise<void> {
  await requestV2.post<void>(`/open-matches/${id}/complete`, { data: {} });
}

export async function cancelOpenMatch(id: number): Promise<void> {
  await requestV2.post<void>(`/open-matches/${id}/cancel`, { data: {} });
}

export async function deleteOpenMatch(id: number): Promise<void> {
  await requestV2.delete<void>(`/open-matches/${id}`);
}

export async function updateOpenMatch(id: number, payload: Partial<OpenMatchModel>): Promise<OpenMatchModel> {
  const body: Record<string, any> = { ...payload };
  // API expects userIds, our model uses participantIds
  if (payload.participantIds) {
    body.userIds = payload.participantIds;
    delete body.participantIds;
  }
  // Normalize eventDate if provided as Date
  if (payload.eventDate) {
    const dateVal: any = payload.eventDate as any;
    if (typeof dateVal === 'object' && typeof dateVal?.toISOString === 'function') {
      body.eventDate = dateVal.toISOString();
    } else {
      body.eventDate = String(dateVal);
    }
  }
  const data = await requestV2.patch<any>(`/open-matches/${id}`, { data: body });
  return toOpenMatch(data);
}
